import { useCallback, useEffect, useState } from "react";
import { GomakePrimaryButton } from "@/components";
import {
  fetchOidcProviders,
  startOidcLogin,
  OidcProvider,
} from "@/services/api-service/auth/oidc-api";

interface OidcProviderButtonsProps {
  /** When present, the invite token is carried through the OIDC state and completes the membership. */
  inviteToken?: string;
  redirectUri?: string;
  returnUrl?: string;
  printHouseId?: string;
  /** Brand-new invitee: render "Sign up with …" and open Cognito's sign-up screen for native GoMake. */
  isSignup?: boolean;
  /** Provider ids to hide (e.g. ["gomake"] when GoMake uses a native password form instead). */
  excludeProviders?: string[];
  onError?: (message: string) => void;
}

/** Turns a provider's "Login with …" label into "Sign up with …" for new invitees. */
const toSignupLabel = (label: string): string => {
  if (/sign up with/i.test(label)) return label;
  if (/login with/i.test(label)) {
    return label.replace(/login with/i, "Sign up with");
  }
  return `Sign up with ${label}`;
};

/**
 * Renders one button per enabled OIDC provider ("Login with GoMake/Google/HP/...").
 * Clicking a provider requests the Cognito Hosted UI authorize URL and redirects the browser.
 */
const OidcProviderButtons = ({
  inviteToken,
  redirectUri,
  returnUrl,
  printHouseId,
  isSignup,
  excludeProviders,
  onError,
}: OidcProviderButtonsProps) => {
  const [providers, setProviders] = useState<OidcProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchOidcProviders()
      .then((list) => {
        if (mounted) setProviders(list);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const visibleProviders = excludeProviders?.length
    ? providers.filter((p) => !excludeProviders.includes(p.id))
    : providers;

  const handleClick = useCallback(
    async (provider: string) => {
      try {
        setPendingProvider(provider);
        const authorizeUrl = await startOidcLogin({
          provider,
          inviteToken,
          redirectUri,
          returnUrl,
          printHouseId,
          signup: isSignup,
          // Return to the domain the user is on so the token lands in this origin's storage.
          originHost:
            typeof window !== "undefined" ? window.location.host : undefined,
        });
        if (authorizeUrl) {
          window.location.href = authorizeUrl;
          return;
        }
        setPendingProvider(null);
        onError?.("Failed to start login. Please try again.");
      } catch (error: any) {
        setPendingProvider(null);
        onError?.(error?.message || "Failed to start login. Please try again.");
      }
    },
    [inviteToken, redirectUri, returnUrl, printHouseId, isSignup, onError],
  );

  if (loading || visibleProviders.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        width: "100%",
      }}
    >
      {visibleProviders.map((provider) => (
        <GomakePrimaryButton
          key={provider.id}
          onClick={() => handleClick(provider.id)}
          disabled={pendingProvider !== null}
        >
          {isSignup ? toSignupLabel(provider.label) : provider.label}
        </GomakePrimaryButton>
      ))}
    </div>
  );
};

export { OidcProviderButtons };
